/**
 * Analytics Service
 * Handles user behavior tracking, performance monitoring, and analytics
 * Provides privacy-friendly analytics with configurable tracking
 */
import config from '../config';
import { logger } from '../utils/logger';

// Analytics event types
interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  userId?: string;
  timestamp?: number;
}

interface PageViewEvent {
  page: string;
  title?: string;
  referrer?: string;
  userId?: string;
}

interface UserEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  userId?: string;
}

interface PerformanceMetrics {
  page: string;
  loadTime: number;
  domContentLoaded: number;
  firstContentfulPaint?: number;
  largestContentfulPaint?: number;
  cumulativeLayoutShift?: number;
  firstInputDelay?: number;
}

/**
 * Analytics Service Class
 * Provides methods for tracking user interactions and performance
 */
class AnalyticsService {
  private isEnabled: boolean;
  private userId: string | null = null;
  private sessionId: string;
  private events: AnalyticsEvent[] = [];
  
  constructor() {
    this.isEnabled = config.FEATURES.ENABLE_ANALYTICS;
    this.sessionId = this.generateSessionId();
    
    // Initialize analytics if enabled
    if (this.isEnabled) {
      this.initializeAnalytics();
    }
  }
  
  /**
   * Initialize analytics tracking
   */
  private initializeAnalytics(): void {
    // Initialize Google Analytics if configured
    if (config.SERVICES.GOOGLE_ANALYTICS_ID) {
      this.initializeGoogleAnalytics();
    }
    
    // Set up performance monitoring
    this.setupPerformanceMonitoring();
    
    // Set up error tracking
    this.setupErrorTracking();
    
    // Track initial page view
    this.trackPageView(window.location.pathname, document.title);
  }
  
  /**
   * Initialize Google Analytics
   */
  private initializeGoogleAnalytics(): void {
    const gaId = config.SERVICES.GOOGLE_ANALYTICS_ID;
    if (!gaId) return;
    
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);
    
    // Initialize gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    const gtag = (...args: any[]) => {
      (window as any).dataLayer.push(args);
    };
    (window as any).gtag = gtag;
    
    gtag('js', new Date());
    gtag('config', gaId, {
      anonymize_ip: true,
      respect_dnt: true,
    });
  }
  
  /**
   * Set up performance monitoring
   */
  private setupPerformanceMonitoring(): void {
    // Monitor Core Web Vitals
    if ('web-vital' in window) {
      // This would integrate with web-vitals library if installed
      // For now, we'll use basic performance API
    }
    
    // Monitor page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.trackPerformanceMetrics();
      }, 0);
    });
  }
  
  /**
   * Set up error tracking
   */
  private setupErrorTracking(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      this.trackError({
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    });
    
    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        message: 'Unhandled Promise Rejection',
        reason: event.reason,
        stack: event.reason?.stack,
      });
    });
  }
  
  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Set the current user ID for tracking
   */
  setUserId(userId: string | null): void {
    this.userId = userId;
    
    if (this.isEnabled && (window as any).gtag) {
      (window as any).gtag('config', config.SERVICES.GOOGLE_ANALYTICS_ID, {
        user_id: userId,
      });
    }
  }
  
  /**
   * Track a page view
   */
  trackPageView(page: string, title?: string): void {
    if (!this.isEnabled) return;
    
    const event: PageViewEvent = {
      page,
      title: title || document.title,
      referrer: document.referrer,
      userId: this.userId || undefined,
    };
    
    // Send to Google Analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_title: event.title,
        page_location: window.location.href,
        page_path: event.page,
      });
    }
    
    // Store locally for batch sending
    this.events.push({
      name: 'page_view',
      properties: event,
      timestamp: Date.now(),
    });
    
    logger.child('ANALYTICS').debug('📊 Page View:', event);
  }
  
  /**
   * Track a user event
   */
  trackEvent(action: string, category: string, label?: string, value?: number): void {
    if (!this.isEnabled) return;
    
    const event: UserEvent = {
      action,
      category,
      label,
      value,
      userId: this.userId || undefined,
    };
    
    // Send to Google Analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
    
    // Store locally
    this.events.push({
      name: 'user_event',
      properties: event,
      timestamp: Date.now(),
    });
    
    logger.child('ANALYTICS').debug('📊 Event:', event);
  }
  
  /**
   * Track prompt interactions
   */
  trackPromptInteraction(action: 'view' | 'like' | 'save' | 'share' | 'copy', promptId: string): void {
    this.trackEvent(action, 'prompt_interaction', `prompt_${promptId}`);
  }
  
  /**
   * Track user authentication events
   */
  trackAuthEvent(action: 'login' | 'logout' | 'register', method?: string): void {
    this.trackEvent(action, 'authentication', method);
  }
  
  /**
   * Track search events
   */
  trackSearch(query: string, resultsCount?: number): void {
    this.trackEvent('search', 'content', query, resultsCount);
  }
  
  /**
   * Track performance metrics
   */
  private trackPerformanceMetrics(): void {
    if (!this.isEnabled || !window.performance) return;
    
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (!navigation) return;
    
    const metrics: PerformanceMetrics = {
      page: window.location.pathname,
      loadTime: navigation.loadEventEnd - navigation.loadEventStart,
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
    };
    
    // Get paint metrics if available
    const paintEntries = performance.getEntriesByType('paint');
    paintEntries.forEach((entry) => {
      if (entry.name === 'first-contentful-paint') {
        metrics.firstContentfulPaint = entry.startTime;
      }
    });
    
    // Send to Google Analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', 'timing_complete', {
        name: 'page_load',
        value: Math.round(metrics.loadTime),
      });
    }
    
    logger.child('ANALYTICS').debug('📊 Performance:', metrics);
  }
  
  /**
   * Track errors
   */
  trackError(error: {
    message: string;
    filename?: string;
    lineno?: number;
    colno?: number;
    stack?: string;
    reason?: any;
  }): void {
    if (!this.isEnabled) return;
    
    // Send to Google Analytics as exception
    if ((window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.message,
        fatal: false,
      });
    }
    
    // Store error details
    this.events.push({
      name: 'error',
      properties: {
        ...error,
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.userId,
        sessionId: this.sessionId,
      },
      timestamp: Date.now(),
    });
    
    logger.child('ANALYTICS').error('📊 Error tracked:', error);
  }
  
  /**
   * Track custom conversion events
   */
  trackConversion(eventName: string, value?: number): void {
    if (!this.isEnabled) return;
    
    if ((window as any).gtag) {
      (window as any).gtag('event', 'conversion', {
        send_to: config.SERVICES.GOOGLE_ANALYTICS_ID,
        event_category: 'conversion',
        event_label: eventName,
        value: value,
      });
    }
    
    this.trackEvent('conversion', 'business', eventName, value);
  }
  
  /**
   * Get stored events (for debugging or batch sending)
   */
  getEvents(): AnalyticsEvent[] {
    return [...this.events];
  }
  
  /**
   * Clear stored events
   */
  clearEvents(): void {
    this.events = [];
  }
  
  /**
   * Enable or disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    
    if (enabled && !this.sessionId) {
      this.initializeAnalytics();
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;